import AutoNumeric from "autonumeric";
import { symbolLookup } from "../../../../TIPro/js/symbolLookup.js";
import { DateHelpers } from "../helpers/date-helpers";
import { FormatHelpers } from "../helpers/format-helpers";
import { StringHelpers } from "../helpers/string-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { AdvancedOEPStateModel } from "../models/chart-style/advanced-oep-state.model";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { BidAskDataModel } from "../models/data-point/bid-ask-data.model";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { Timeframe } from "../models/enum/timeframe.enum";
import { SingleStockWindowChangeFromCloseManager } from "../models/single-stock-window/single-stock-window-change-from-close.manager";
import { TradeAccountModel } from "../models/trade/trade-account.model";
import { QuoteType, TradeModel, TradeOrderSource, TradeOrderType } from "../models/trade/trade.model";
import { InformationPopupComponent } from "./information-popup.component";
import { PnlComponent } from "./pnl.component";
import { TradeComponent } from "./trade.component";
import { TradeOrderTypeComponent } from "./menu/trade-order-type.component";
import { ColoredLastPriceComponent, ColoredLastPriceSource } from "./colored-last-price.component";

declare const $: any;

enum AdvancedTradeType {
    SellMarket = 'SellMarket',
    BuyMarket = 'BuyMarket',
    SellBid = 'SellBid',
    BuyAsk = 'BuyAsk',
    JoinBid = 'JoinBid',
    JoinAsk = 'JoinAsk',
    CancelOrder = 'CancelOrder',
    LimitIn = 'LimitIn',
}

export class AdvancedTradeComponent {
    public id = StringHelpers.newGuid();

    public enabled = true;

    private element: HTMLElement;
    private changeFromCloseElement: HTMLElement | null = null;
    private accountSelectListElement: HTMLElement | null = null;
    private autoSendElement: HTMLInputElement | null = null;
    private symbolSwitcher: HTMLInputElement | null = null;
    private limitInGroup: HTMLElement | null = null;
    private arrowExpand: HTMLElement | null = null;
    private limitInElement: HTMLElement | null = null;
    private exportButtonElement: HTMLElement | null = null;

    private base: IChartContainer;
    private coloredLastPrice: ColoredLastPriceComponent | null = null;
    private bidSize = 0;
    private bidCount = 0;
    private askSize = 0;
    private askCount = 0;
    private bidPercentage = 0;
    private askPercentage = 0;
    private symbol = '';
    private turnToLookupModeCount = 0;

    private chartStyle: ChartStyleSettingsModel | null = null;
    private singleStockWindowChangeFromCloseManager: SingleStockWindowChangeFromCloseManager | null = null;

    private quoteQuantityInput: AutoNumeric | null = null;
    private limitInInput: AutoNumeric | null = null;
    private limitInBuy = true;

    private model = new TradeModel();
    private firstBidAskModel: BidAskDataModel | null = null;
    private currentBidAskModel = new BidAskDataModel();
    private bidAskTableData: Array<BidAskDataModel> = [];
    private accounts: Array<TradeAccountModel> = [];
    private tradeOrders: Array<TradeOrderDataModel> = [];
    private lastSubmittedOrder: any = {};

    private chartSymbolLookup: any = null;
    private preventPersistingAccount = false;
    private preventPersistingShares = false;
    private accountRendered = false;
    private moving = false;
    private submitting = false;
    private editing = false;
    private dragging = false;
    private dragEnabled = false;
    private tosAutoExpanded = false;
    private displayCFCPercentage = true;
    private showDetailedView = false;
    private limitInPriceFromLastPriceUpdate = false;
    private autoCollapseTimeoutId: number | null = null;

    private readonly defaultUpColor = 'rgb(0, 255, 0)';
    private readonly defaultDownColor = 'rgb(255, 0, 0)';

    private readonly DEFAULT_TOP = '200px';
    private readonly DEFAULT_LEFT = '20px';

    constructor(base: IChartContainer) {
        this.id = StringHelpers.newGuid();
        this.base = base;

        this.singleStockWindowChangeFromCloseManager = this.base.getComponentsByType(SingleStockWindowChangeFromCloseManager)?.[0] as SingleStockWindowChangeFromCloseManager;

        this.subscribeChartDataService();
        this.renderHtml();
        this.init();
    }

    public show() {
        this.addActive();

        this.base.chartService.requestOpenRobotTrade();
        this.saveUIState();
    }

    public hide() {
        this.element.classList.remove('active');
        this.saveUIState();
    }

    public isActive(): boolean {
        return this.element.classList.contains('active');
    }

    public editOff() {
        this.editing = false;
    }
    
    private addActive() {
        if (!this.enabled) return;

        this.element.classList.add('active');
    }

    public getQuoteType(): QuoteType {
        return this.model.quoteType;
    }

    public setQuoteType(quoteType: QuoteType) {
        if (this.editing) return;
        
        this.model.quoteType = quoteType;
        this.setSelectListValue('.select-list.quote-type li', this.model.quoteType);
    }

    public setQuoteQuantity(value: number) {
        if (this.editing) return;

        this.model.quoteQuantity = value;
        this.quoteQuantityInput?.set(value);
    }

    public setShares(value: number) {
        if (value <= 0 || document.activeElement === this.quoteQuantityInput.node() || this.submitting || this.editing) return;

        this.preventPersistingShares = true;
        this.setQuoteType(QuoteType.Shares);
        this.setQuoteQuantity(value);
    }

    private init() {
        this.calculateChangeFromClose();
        this.initAutoNumberic();
        this.onQuantityGroupClick();
        this.onSelectListClick();
        this.onSelectListItemClick();
        this.onClickTradeButtons();
        this.onClickBidAskButtons();
        this.onClickLimitInTradeButtons();
        this.onResize();
        this.onBodyClick();
        this.onExtreamChange();
        this.onArrowExpandClick();
        this.initDataTable();
        this.onDrag();
        this.onClickSwapSymbolLookupMode();
        this.onClickCFC();
        this.onExportClick();
        this.onDocumentReady();
        this.onTradeComponentChange();
        this.onAutosendChange();

        this.setDefaults();
    }

    private onTradeComponentChange() {
        if (this.chartStyle?.advancedOEPPopoutMode) {
            return;
        }

        const tradeComponent = this.base.getComponentsByType(TradeComponent)[0] as TradeComponent;
        const tradeOrderTypeComponents = this.base.getComponentsByType(TradeOrderTypeComponent) as Array<TradeOrderTypeComponent>;
        tradeOrderTypeComponents.forEach((component: TradeOrderTypeComponent) => {
            component.onChange(() => {
                const model = tradeComponent.getTradeModel();
                this.setQuoteType(model.quoteType);
                this.setQuoteQuantity(model.quoteQuantity);
                this.setAccount(model.account);
            });
        });

        tradeComponent?.onChange(() => {
            const model = tradeComponent.getTradeModel();
            this.setQuoteType(model.quoteType);
            this.setQuoteQuantity(model.quoteQuantity);
            this.setAccount(model.account);
        });
    }

    private onAutosendChange() {
        this.autoSendElement?.addEventListener('change', () => {
            this.saveUIState();
        });
    }

    private setTradeComponentValues() {
        if (this.chartStyle?.advancedOEPPopoutMode) {
            return;
        }

        const tradeComponent = this.base.getComponentsByType(TradeComponent)[0] as TradeComponent;
        tradeComponent.setQuoteType(this.model.quoteType);
        tradeComponent.setQuoteQuantity(this.model.quoteQuantity);
        tradeComponent.setAccount(this.model.account);
        tradeComponent.notifyChangeCallbacks();
    }

    private onDocumentReady() {
        const ctx = this;
        $(document).ready(function () {
            let manuallyLoaded = false;
            $.ajax({
                type: "GET",
                url: "/ajax/symbol.php",
                success: function () {
                    ctx.chartSymbolLookup = new symbolLookup("avoep-chart-symbol-lookup", false, "chartSymbolLookup", (symbol: any) => {
                        ctx.requestSymbol(ctx.symbolSwitcher.value);
                    });
                },
                error: function () {
                    manuallyLoaded = true;
                }
            });

            $(ctx.symbolSwitcher).keyup(function (e) {
                if (e.keyCode == 13 && manuallyLoaded) {
                    ctx.requestSymbol(ctx.symbolSwitcher.value);
                }

                // if (!ctx.element.classList.contains('table-expanded')) {
                //     ctx.tosAutoExpanded = true;
                //     ctx.arrowExpand?.click();
                // }
            });
        });
    }

    private requestSymbol(symbol: string) {
        if (!symbol) { return; }

        this.changeFromCloseElement?.setInnerHtml('&nbsp;');
        this.coloredLastPrice?.reset();

        symbol = symbol.toUpperCase();

        this.updateSymbolLabel();

        const chartContainer = this.base as any;
        chartContainer.chartService.requestNewSymbol(symbol);
        chartContainer.pnpContainer?.chartService?.requestNewSymbol(symbol);
        this.element.querySelector('.last-price-symbol-wrapper')?.classList.remove('lookup');

        // Schedule auto-collapse of TOS if it was auto-expanded
        this.scheduleAutoCollapseTOS(true, 3);
    }

    private onClickSwapSymbolLookupMode() {
        const wrapper = this.element.querySelector('.last-price-symbol-wrapper') as HTMLElement;
        wrapper?.findFirst('.symbol-wrapper')?.ifExistThen((el: HTMLElement) => {
            el.addEventListener('click', () => {
                this.symbolSwitcher?.setValue(this.symbol);
                wrapper.classList.add('lookup');
                this.turnToLookupModeCount++;

                setTimeout(() => {
                    if (this.turnToLookupModeCount >= 3) {
                        el.removeAttribute('title');
                    }

                    if (this.chartSymbolLookup) {
                        this.chartSymbolLookup.setSymbolFocus();
                    }
                }, 150);
            });
        });

        wrapper?.findFirst('.icon-close')?.addEventListener('click', (e: any) => {
            wrapper.classList.remove('lookup');
        });
    }

    private onClickCFC() {
        const wrapper = this.element.querySelector('.pnl-value') as HTMLElement;
        wrapper?.addEventListener('click', () => {
            this.displayCFCPercentage = !this.displayCFCPercentage;
            this.calculateChangeFromClose();
            this.saveUIState();
        });
    }

    private onExportClick() {
        this.exportButtonElement?.addEventListener('click', () => {
            this.exportBidAskTableToCSV();
        });
    }

    private exportBidAskTableToCSV() {
        if (!this.chartStyle?.advancedOEPTOSDevModeEnabled) {
            return;
        }

        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += "Symbol,Trade Time,Trade Price,Trade Size,Quote Time,Bid,Ask,Bid Size,Ask Size,Bid Participants,Ask Participants\n";

        this.bidAskTableData.forEach((data) => {
            const row =
                `${data.symbol},` +
                `${DateHelpers.format('HH:MM:SS.fff', data.tradeTime)},` +
                `${data.tradePrice},` +
                `${data.tradeSize},` +
                `${DateHelpers.format('HH:MM:SS.fff', data.quoteTime)},` +
                `${data.bid},` +
                `${data.ask},` +
                `${data.bidParticipantSize},` +
                `${data.askParticipantSize},` +
                `${data.bidParticipantCount},` +
                `${data.askParticipantCount}\n`;
            csvContent += row;
        });

        if (this.firstBidAskModel) {
            const row =
                `${this.firstBidAskModel.symbol},` +
                `${DateHelpers.format('HH:MM:SS.fff', this.firstBidAskModel.tradeTime)},` +
                `${this.firstBidAskModel.tradePrice},` +
                `${this.firstBidAskModel.tradeSize},` +
                `${DateHelpers.format('HH:MM:SS.fff', this.firstBidAskModel.quoteTime)},` +
                `${this.firstBidAskModel.bid},` +
                `${this.firstBidAskModel.ask},` +
                `${this.firstBidAskModel.bidParticipantSize},` +
                `${this.firstBidAskModel.askParticipantSize},` +
                `${this.firstBidAskModel.bidParticipantCount},` +
                `${this.firstBidAskModel.askParticipantCount}\n`;
            csvContent += row;
        }

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `export_TOS_${this.symbol}_${DateHelpers.format('yyyymmdd_HHMMSS', new Date())}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    private requestingSymbol = true;
    private subscribeChartDataService() {
        this.base.chartService.subscribeChartStyleData((data: ChartStyleSettingsModel) => {
            let needToSet = false;
            if (!this.chartStyle || this.chartStyle.chartSettingsPopupModel?.defaultEnableTradeOrderEntry != this.enabled) {
                needToSet = true;
            }

            this.chartStyle = data;

            if (needToSet)
                this.setPropertiesFromChartStyles();
        });
        this.base.chartService.subscribeLastPriceData((price: number) => {
            if (this.requestingSymbol) return;

            this.calculateChangeFromClose();
            this.updateLimitInPriceFromLastPriceUpdate();
        });
        this.base.chartService.subscribe_TradeAccounts((accounts: Array<TradeAccountModel>) => {
            this.accounts = accounts;
            this.renderAccountList();
        });
        this.base.chartService.subscribe_LastTradeAccounts((account: string) => {
            this.editing = false;
            this.setAccount(account);
        });
        this.base.chartService.subscribeChartRequestingNewSymbolData((symbol: string, timeframe: Timeframe) => {
            if (!this.enabled) return;
            this.symbol = symbol;
            this.currentBidAskModel = new BidAskDataModel();
            this.firstBidAskModel = null;
            this.bidAskTableData = [];
            this.requestingSymbol = false;
            this.editing = false;
            this.limitInPriceFromLastPriceUpdate = false;
            this.changeFromCloseElement?.setInnerHtml('')
            this.coloredLastPrice?.reset();
            
            this.updateSymbolLabel();
            this.updatePriceLabel();
            this.updateDataTable();
        });
        this.base.chartService.subscribeAllTradeOrdersData((tradeOrders: Array<TradeOrderDataModel>) => {
            this.tradeOrders = tradeOrders;
            this.validateLimitOrderExist();
        });
        this.base.chartService.subscribe_CancelExecuteTradeImmediately(() => {
            this.updateTradeOrderPanel(null);
        });
        this.base.chartService.subscribe_BidAsk((data: BidAskDataModel) => {
            this.currentBidAskModel = data;

            if (!this.firstBidAskModel) {
                this.firstBidAskModel = data;
            }

            // Check if this exact data already exists in bidAskTableData or matches firstBidAskModel
            const isDuplicate = this.bidAskTableData.some(existing =>
                existing.tradeTime.valueOf() === data.tradeTime.valueOf() &&
                existing.tradePrice === data.tradePrice &&
                existing.tradeSize === data.tradeSize
            ) || (
                    this.firstBidAskModel &&
                    this.firstBidAskModel.tradeTime.valueOf() === data.tradeTime.valueOf() &&
                    this.firstBidAskModel.tradePrice === data.tradePrice &&
                    this.firstBidAskModel.tradeSize === data.tradeSize
                );

            // Only add if not duplicate
            if (!isDuplicate) {
                this.bidAskTableData.unshift(data);

                // Limit the number of rows to display (most recent entries)
                const maxRows = this.chartStyle?.advancedOEPTOSCount || 100;
                if (this.bidAskTableData.length > maxRows) {
                    this.bidAskTableData = this.bidAskTableData.slice(0, maxRows);
                }
            }
            
            this.calculateChangeFromClose();
            this.updatePriceLabel();
            this.updateDataTable();
        });
    }

    public setAccount(value: string) {
        if (this.editing) return;

        const acccountElement = this.accountSelectListElement.querySelector<HTMLElement>('li[data-value="' + value + '"]');
        if (acccountElement) {
            this.preventPersistingAccount = true;
            this.selectAccount(acccountElement);
        }
    }

    private onQuantityGroupClick() {
        this.element.querySelectorAll<HTMLElement>('.quantity-group .value').forEach((el: HTMLElement) => {
            el.addClass('active');
            el.addEventListener('click', () => {
                if (this.moving) return;

                const value = parseInt(el.getAttribute('data-value') || '0', 10);
                if (this.quoteQuantityInput && value > 0) {
                    this.editing = true;
                    this.quoteQuantityInput.set(value);
                    this.model.quoteQuantity = value;
                }
            });
        });
    }

    private onSelectListClick() {
        this.element.querySelectorAll<HTMLElement>('.select-list').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                if (this.moving || el.classList.contains('disabled')) return;
                const isActive = el.classList.contains('active');
                this.removeAllActiveSelectList();
                if (!isActive) {
                    el.classList.add('active');
                }
            });
        });
    }

    private onSelectListItemClick() {
        this.element.querySelectorAll<HTMLElement>('.select-list ul li').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                const parent = el.closest('.select-list');
                if (parent) {
                    const label = parent.querySelector('p');
                    if (label) {
                        label.innerHTML = el.innerHTML;
                    }

                    if (parent.classList.contains('quote-type')) {
                        this.preventPersistingShares = false;
                        this.model.quoteType = el.dataset.value;
                        this.editing = true;
                        this.setTradeComponentValues();
                    }
                }
            });
        });
    }

    private removeAllActiveSelectList() {
        this.element.querySelectorAll<HTMLElement>('.select-list').forEach((el: HTMLElement) => {
            el.classList.remove('active');
        });
    }

    private renderAccountList() {
        if (!this.accounts?.length || this.accountSelectListElement.classList.contains('active')) {
            return;
        }

        let html = '';
        for (let i = 0; i < this.accounts.length; i++) {
            html += `<li data-value="${this.accounts[i].name}">${this.accounts[i].privacyName}</li>`;
        }

        const ul = this.accountSelectListElement.querySelector('ul');
        if (ul) {
            ul.innerHTML = html;
        }

        this.accountSelectListElement.classList.remove('hidden');
        this.accountSelectListElement.classList.remove('disabled');

        setTimeout(() => {
            this.accountSelectListElement.querySelectorAll('li').forEach((el: HTMLElement) => {
                el.addEventListener('click', () => {
                    this.preventPersistingAccount = false;
                    this.editing = true;
                    this.selectAccount(el);
                    this.setPnlSelectedAccount();
                    this.setTradeComponentValues();
                });
            });

            if (!this.accountRendered) {
                this.accountRendered = true;
                this.setAccountFromChartStyles();
            } else {
                const accountElement = this.accountSelectListElement.findFirst(`li[data-value="${this.model.account}"]`);
                if (!accountElement || (this.model.account == accountElement.dataset.value && this.model.accountLabel == accountElement.innerHTML)) {
                    return;
                }

                this.selectAccount(accountElement);
            }
        }, 200);
    }

    private selectAccount(el: HTMLElement) {
        const parent = el.closest('.select-list');
        if (parent) {
            const label = parent.querySelector('p');
            if (label) {
                label.innerHTML = el.innerHTML;
            }
        }

        this.model.account = el.dataset.value;
        this.model.accountLabel = el.innerHTML;
    }

    private initAutoNumberic() {
        const quoteQuantityInputElement = this.element.querySelector<HTMLElement>('.form-group .quote-quantity-input input');
        if (quoteQuantityInputElement) {
            const sharesIncrement = 100;
            this.quoteQuantityInput = new AutoNumeric(quoteQuantityInputElement, this.model.quoteQuantity, {
                decimalPlaces: 0,
                minimumValue: '0',
                wheelStep: sharesIncrement
            });

            this.quoteQuantityInput?.node().addEventListener('keyup', () => {
                this.preventPersistingShares = false;
                this.editing = true;
                this.model.quoteQuantity = this.quoteQuantityInput.getNumber();
                this.setTradeComponentValues();
            });

            const upButton = this.element.querySelector<HTMLElement>('.form-group .quote-quantity-input .up');;
            const downButton = this.element.querySelector<HTMLElement>('.form-group .quote-quantity-input .down');
            upButton?.addEventListener('click', () => {
                this.preventPersistingShares = false;
                const value = this.quoteQuantityInput.getNumber() + sharesIncrement;
                this.editing = true;
                this.quoteQuantityInput.set(value);
                this.model.quoteQuantity = value;
                this.setTradeComponentValues();
            });

            downButton?.addEventListener('click', () => {
                this.preventPersistingShares = false;
                const value = this.quoteQuantityInput.getNumber() - sharesIncrement;
                this.editing = true;
                this.quoteQuantityInput.set(value);
                this.model.quoteQuantity = value;
                this.setTradeComponentValues();
            });
        }


        const limitInInputElement = this.element.querySelector<HTMLElement>('.form-group .limit-in-input input');
        if (limitInInputElement) {
            const increment = 0.01;
            this.limitInInput = new AutoNumeric(limitInInputElement, 0, {
                decimalPlaces: 2,
                minimumValue: '0',
                wheelStep: increment
            });

            this.limitInInput?.node().addEventListener('keyup', () => {
                this.preventPersistingShares = false;
            });

            const upButton = this.element.querySelector<HTMLElement>('.form-group .limit-in-input .up');;
            const downButton = this.element.querySelector<HTMLElement>('.form-group .limit-in-input .down');
            upButton?.addEventListener('click', () => {
                this.preventPersistingShares = false;
                const value = this.limitInInput.getNumber() + increment;
                this.limitInInput.set(value);
            });

            downButton?.addEventListener('click', () => {
                this.preventPersistingShares = false;
                const value = this.limitInInput.getNumber() - increment;
                this.limitInInput.set(value);
            });
        }
    }

    private setPropertiesFromChartStyles() {
        if (this.chartStyle.advancedOEPSupported && this.chartStyle.advancedOEPEnabled) {
            this.enabled = true;
        } else {
            this.enabled = false;
            this.hide();
        }

        if (!this.enabled) return;


        if (this.chartStyle?.advancedOEPTOSDevModeEnabled) {
            this.exportButtonElement?.parentElement?.classList.add('active');
            this.showDetailedView = true;
            this.element.addClass('dev-mode');
            this.initDataTable();
        }

        if (!this.chartStyle.advancedOEPStateModel) {
            this.chartStyle.advancedOEPStateModel = new AdvancedOEPStateModel();
        }

        if (this.chartStyle.advancedOEPStateModel.expandedTable) {
            this.element.classList.add('table-expanded');
        }

        // if (this.chartStyle.advancedOEPStateModel.expandedLimitIn) {
        //     this.displayLimitInSection(true);
        // }

        if (this.chartStyle.advancedOEPStateModel.autoSend) {
            this.autoSendElement.checked = true;
        }

        this.displayCFCPercentage = this.chartStyle.advancedOEPStateModel.displayCFCPercentage;

        // advancedOEPPopoutMode is for popout/standalone mode (e.g., from Brokerage Plus)
        if (this.chartStyle.advancedOEPPopoutMode) {
            this.base.body.addClass('advanced-oep-enabled');
            this.addActive();

            this.setAccountFromChartStyles();
            this.calculateChangeFromClose();
            this.setDefaults();
            setTimeout(() => this.adjustBidAskTableWidthForPopout(), 0);
            return;
        }

        this.dragEnabled = true;
        if (this.chartStyle.advancedOEPStateModel.left != null && this.chartStyle.advancedOEPStateModel.top != null) {
            this.element.style.left = this.chartStyle.advancedOEPStateModel.left + 'px';
            this.element.style.top = this.chartStyle.advancedOEPStateModel.top + 'px';
        } else {
            this.element.style.left = this.DEFAULT_LEFT;
            this.element.style.top = this.DEFAULT_TOP;
        }

        if (this.chartStyle.chartSettingsPopupModel?.defaultQuoteType) {
            this.model.defaultQuoteType = this.chartStyle.chartSettingsPopupModel?.defaultQuoteType as QuoteType;
        }

        if (this.chartStyle.chartSettingsPopupModel?.defaultQuoteQuantity) {
            this.model.quoteQuantity = this.chartStyle.chartSettingsPopupModel?.defaultQuoteQuantity;
        }

        if (this.chartStyle.advancedOEPOpen) {
            this.addActive();
        }

        this.setAccountFromChartStyles();
        this.calculateChangeFromClose();
        this.setDefaults();
    }

    private setAccountFromChartStyles() {
        if (this.chartStyle) {
            let accountElement: HTMLElement | null = null;
            if (this.chartStyle.newTradeUIAccount) {
                accountElement = this.accountSelectListElement.querySelector<HTMLElement>('li[data-value="' + this.chartStyle.newTradeUIAccount + '"]');
            }

            //fallback to first account
            if (!accountElement) {
                accountElement = this.accountSelectListElement.querySelector<HTMLElement>('li');
            }

            if (accountElement) {
                this.selectAccount(accountElement);
            }
        }
    }

    private updateTradeOrderPanel(trade: TradeModel | null) {
        let html = '';
        if (trade) {
            html = 'OPEN - ';
            if (trade.isBuy) {
                html += '<span class="bold green">BUYING</span> ';
            } else {
                html += '<span class="bold red">SELLING</span> ';
            }

            let price = '';
            const quoteQuantity = `${FormatHelpers.numberToStringThousandSeparators(trade.quoteQuantity, true)}`;
            html += `${trade.quoteType == QuoteType.Dollars ? `$${quoteQuantity}` : `${quoteQuantity} Shares`} at <span class="bold">`;
            if (trade.orderType == TradeOrderType.Market) {
                html += 'Market';
            } else {
                if (this.lastSubmittedOrder && !this.lastSubmittedOrder.limitIn) {
                    if (trade.isBuy) {
                        html += 'BID';
                    }
                    else {
                        html += 'ASK';
                    }
                }

                price = `${FormatHelpers.numberToStringThousandSeparators(trade.price, true)}`;
            }

            html += `</span> ${price}`;
        } else {
            html += '&nbsp;';
            this.lastSubmittedOrder = null;
        }

        this.element.findFirst('.advanced-trade-label')?.setInnerHtml(html);
    }

    private updateTradeOrderPanelFromOpenOrder(trade: TradeOrderDataModel) {
        let html = '';
        if (trade) {
            html = 'OPEN - ';
            if (trade.isLong) {
                html += '<span class="bold green">BUYING</span> ';
            } else {
                html += '<span class="bold red">SELLING</span> ';
            }

            let price = '';
            const quoteQuantity = `${FormatHelpers.numberToStringThousandSeparators(trade.shares, true)}`;
            html += `${quoteQuantity} Shares at <span class="bold">`;
            if (trade.tradeType == TradeOrderType.Market) {
                html += 'Market';
            } else {
                price = `${FormatHelpers.numberToStringThousandSeparators(trade.price, true)}`;
            }

            html += `</span> ${price}`;
        } else {
            html += '&nbsp;';
            this.lastSubmittedOrder = null;
        }

        this.element.findFirst('.advanced-trade-label')?.setInnerHtml(html);
    }

    private onClickTradeButtons() {
        this.element.querySelectorAll<HTMLElement>('.advanced-trade-btn').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                if (this.moving || this.submitting || el.classList.contains('disabled')) return;

                const tradeType = el.getAttribute('data-value') as AdvancedTradeType;
                if (tradeType == AdvancedTradeType.CancelOrder) {
                    this.cancelOpenOrder();
                    return;
                } else if (tradeType == AdvancedTradeType.LimitIn) {
                    this.toggleLimitInInput();
                    return;
                }

                this.submitting = true;

                const bidPrice = this.currentBidAskModel.bid;
                const askPrice = this.currentBidAskModel.ask;
                const submitModel = new TradeModel(this.model);
                submitModel.source = TradeOrderSource.chart_advanced_oep;
                if (tradeType == AdvancedTradeType.SellMarket) {
                    submitModel.isBuy = false;
                    submitModel.orderType = TradeOrderType.Market;
                    submitModel.isFromOEP = true;
                } else if (tradeType == AdvancedTradeType.BuyMarket) {
                    submitModel.isBuy = true;
                    submitModel.orderType = TradeOrderType.Market;
                    submitModel.isFromOEP = true;
                } else if (tradeType == AdvancedTradeType.SellBid) {
                    submitModel.isBuy = false;
                    submitModel.orderType = TradeOrderType.Limit;
                    submitModel.price = bidPrice;
                } else if (tradeType == AdvancedTradeType.BuyAsk) {
                    submitModel.isBuy = true;
                    submitModel.orderType = TradeOrderType.Limit;
                    submitModel.price = askPrice;
                } else if (tradeType == AdvancedTradeType.JoinBid) {
                    submitModel.isBuy = true;
                    submitModel.orderType = TradeOrderType.Limit;
                    submitModel.price = bidPrice;
                } else if (tradeType == AdvancedTradeType.JoinAsk) {
                    submitModel.isBuy = false;
                    submitModel.orderType = TradeOrderType.Limit;
                    submitModel.price = askPrice;
                }

                this.submitTradeOrder(submitModel);
            });
        });
    }

    private onClickBidAskButtons() {
        const bidElement = this.element.querySelector('.bid') as HTMLElement;
        bidElement?.addEventListener('click', () => {
            const bidPrice = this.currentBidAskModel.bid;
            this.setLimitInInputPrice(bidPrice);
        });

        const askElement = this.element.querySelector('.ask') as HTMLElement;
        askElement?.addEventListener('click', () => {
            const askPrice = this.currentBidAskModel.ask;
            this.setLimitInInputPrice(askPrice);
        });
    }

    private onClickLimitInTradeButtons() {
        const group = this.element.querySelector<HTMLElement>('.form-group.limit-in');
        if (!group) return;

        const buyButton = group.querySelector<HTMLElement>('.button.buy');
        const sellButton = group.querySelector<HTMLElement>('.button.sell');
        buyButton?.addEventListener('click', () => {
            this.limitInBuy = true;
            group.removeClass('s');
            group.addClass('b');
        });

        sellButton?.addEventListener('click', () => {
            this.limitInBuy = false;
            group.removeClass('b');
            group.addClass('s');
        });

        const submitButton = group.querySelector<HTMLElement>('.button.submit');
        submitButton?.addEventListener('click', () => {
            if (this.moving || this.submitting || submitButton.classList.contains('disabled')) return;

            this.submitting = true;
            const submitModel = new TradeModel(this.model);
            submitModel.source = TradeOrderSource.chart_advanced_oep;
            if (this.limitInBuy) {
                submitModel.isBuy = true;
                submitModel.orderType = TradeOrderType.Limit;
                submitModel.price = this.limitInInput?.getNumber() ?? 0;
            } else {
                submitModel.isBuy = false;
                submitModel.orderType = TradeOrderType.Limit;
                submitModel.price = this.limitInInput?.getNumber() ?? 0;
            }

            this.submitTradeOrder(submitModel);
        });
    }

    private submitTradeOrder(submitModel: TradeModel, limitIn: boolean = false) {
        const shouldShowConfirmation = this.shouldConfirmAutoSend();
        if (!limitIn && submitModel.orderType === TradeOrderType.Limit) {
            this.setLimitInInputPrice(submitModel.price);
        }

        submitModel.autoSend = !shouldShowConfirmation;
        submitModel.submitTradeCustomConfirmation(this.base, shouldShowConfirmation, false, () => {
            this.updateTradeOrderPanel(submitModel);

            this.processTradeOrderSubmission(submitModel, limitIn);
        }, () => {
            this.processTradeOrderSubmission(submitModel, limitIn);
        });
    }

    private shouldConfirmAutoSend() {
        return this.autoSendElement ? !this.autoSendElement.checked : true;
    }

    private processTradeOrderSubmission(submitModel: TradeModel, limitIn: boolean) {
        if (submitModel.orderType === TradeOrderType.Limit) {
            let now = new Date();
            this.lastSubmittedOrder = {
                price: submitModel.price,
                isBuy: submitModel.isBuy,
                limitIn: limitIn,
                timestamp: now
            };
        } else {
            this.lastSubmittedOrder = null;
        }

        this.setPnlLastTradeAccount(submitModel.account);
        this.submitting = false;
    }

    private setPnlSelectedAccount() {
        const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
        if (pnl) {
            pnl.setSelectedAccountFromCurrentPositionActiveElement();
        }
    }

    private setPnlLastTradeAccount(account: string) {
        const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
        if (pnl) {
            pnl.lastTradeAccount = account;
            pnl.setSelectPositionByAccount(account);
        }
    }

    private toggleLimitInInput() {
        const display = this.limitInGroup.classList.contains('hidden');
        this.displayLimitInSection(display);
    }

    private displayLimitInSection(display: boolean) {
        if (!display) {
            this.limitInElement?.classList.add('hidden');
            this.updateDataTableHeight('285px');
            this.setLimitInInputPrice(0);
        } else {
            this.setLimitInInputPrice(this.coloredLastPrice?.getLastPrice() ?? 0);
            this.limitInElement?.classList.remove('hidden');
            this.updateDataTableHeight('314px');

        }

        this.saveUIState();
    }

    private updateLimitInPriceFromLastPriceUpdate() {
        const lastPriceValue = this.coloredLastPrice?.getLastPrice() ?? 0;
        if (this.limitInPriceFromLastPriceUpdate || lastPriceValue <= 0) {
            return;
        }

        if (this.limitInElement && !this.limitInElement.classList.contains('hidden')) {
            this.setLimitInInputPrice(lastPriceValue);
        }

        this.limitInPriceFromLastPriceUpdate = true;
    }

    private setLimitInInputPrice(price: number) {
        if (price <= 0 || price > this.chartStyle?.advancedOEPTOSFourDecimalLessThan) {
            this.limitInInput?.update({ decimalPlaces: 2 });
        } else {
            this.limitInInput?.update({ decimalPlaces: 4 });
        }

        this.limitInInput?.set(price);
    }

    private updateDataTableHeight(height: string) {
        const tBody = this.element.querySelector<HTMLElement>('.dt-scroll-body');
        if (tBody) {
            tBody.style.height = height;
            tBody.style.minHeight = height;
        }
    }

    private cancelOpenOrder() {
        const order = this.getSubmittedLimitOrder() || this.getLastOpenOrder();
        if (!order) return;

        const shouldAutoSend = !this.shouldConfirmAutoSend();
        if (this.base.chartService.hasRemoveChartTradeV2()) {
            if (shouldAutoSend) {
                this.removeChartTrade(order);
            } else {
                const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
                popup?.show(`Cancel Order`, order.getCancelOrderMessage(), () => {
                    this.removeChartTrade(order);
                });
            }
        } else {
            this.base.chartService.requestRemoveChartTrade(order.id);
            this.updateTradeOrderPanel(null);
        }
    }

    private removeChartTrade(order: TradeOrderDataModel) {
        if (order.tradeType.toLowerCase() == 'market') {
            const lastPriceValue = this.coloredLastPrice?.getLastPrice() ?? 0;
            const tradeIsBuy = !order.isLong;
            const tradeModel = new TradeModel({
                price: lastPriceValue,
                isBuy: tradeIsBuy,
                orderType: TradeOrderType.Market,
                quoteType: QuoteType.Shares,
                account: order.accountName,
                quoteQuantity: Math.abs(order.shares)
            });
            
            this.base.chartService.requestManualTrade(tradeModel);
        } else{
            this.base.chartService.requestRemoveChartTradeV2(order.id);
        }
        
        this.updateTradeOrderPanel(null);
    }

    private validateLimitOrderExist() {
        const openOrder = this.getLastOpenOrder();
        if (openOrder) {
            this.updateTradeOrderPanelFromOpenOrder(openOrder);
            return;
        }

        this.updateTradeOrderPanel(null);
    }

    private readonly inactiveStatuses = new Set(['canceled', 'rejected', 'filled']);

    private isOrderActive(to: TradeOrderDataModel): boolean {
        const status = to.statusText.toLowerCase();
        const tradeType = to.tradeType.toLowerCase();
        return !this.inactiveStatuses.has(status)
            && !to.ocaGroup
            && (tradeType === 'limit' || tradeType === 'market');
    }

    private getLastOpenOrder() {
        return this.getTradeOrders().find(to => this.isOrderActive(to)) ?? null;
    }

    private getSubmittedLimitOrder() {
        if (!this.lastSubmittedOrder) return null;

        const originalTradeOrders = this.getTradeOrders();
        const tradeOrders = originalTradeOrders
            .filter(to => to.statusText.toLowerCase() == 'submitted' && to.tradeType.toLowerCase() == 'limit'
                && to.price == this.lastSubmittedOrder.price
                && to.isLong == this.lastSubmittedOrder.isBuy
                && !!to.entryTime);

        const order = tradeOrders?.[0];
        return order;
    }

    private getTradeOrders(): Array<TradeOrderDataModel> {
        return this.tradeOrders.filter(x => x.accountName == this.model.account)
            .sort(((a, b) => parseInt(a.id) - parseInt(b.id)));
    }

    private setDefaults() {
        this.model.price = this.model.defaultPrice;

        if (this.quoteQuantityInput) {
            this.quoteQuantityInput.set(this.model.quoteQuantity);
        }

        this.model.orderType = this.model.defaultOrderType;
        this.element.querySelectorAll<HTMLElement>('.trade-options .opt').forEach((el: HTMLElement) => {
            if (el.getAttribute('data-value') == this.model.defaultOrderType) {
                el.classList.add('active');
            } else {
                el.classList.remove('active');
            }
        });

        this.model.quoteType = this.model.defaultQuoteType;
        this.setSelectListValue('.select-list.quote-type li', this.model.defaultQuoteType);
        this.updatePriceLabel();
    }

    private setSelectListValue(querySelector: string, defaultValue: string) {
        this.element.querySelectorAll<HTMLElement>(querySelector).forEach((el: HTMLElement) => {
            if (el.getAttribute('data-value') == defaultValue) {
                const parent = el.closest('.select-list')?.querySelector('p');
                if (parent) {
                    parent.innerHTML = el.innerHTML;
                }

                el.classList.add('active');
            } else {
                el.classList.remove('active');
            }
        });
    }

    private calculateChangeFromClose() {
        let changesFromCloseDollar = 0;
        let changesFromClosePercentage = 0;

        if (this.chartStyle?.changeFromCloseUseTradeQuote) {
            changesFromCloseDollar = this.currentBidAskModel.dollarChange;
            changesFromClosePercentage = this.currentBidAskModel.percentChange;
        } else {
            if (!this.singleStockWindowChangeFromCloseManager) return;

            const changesValues = this.singleStockWindowChangeFromCloseManager.singleStockValueData;
            if (!changesValues) { this.changeFromCloseElement?.setInnerHtml('&nbsp;'); return; }

            changesFromCloseDollar = changesValues.changesFromCloseDollar;
            changesFromClosePercentage = changesValues.changesFromClosePercentage;
        }

        let lastPriceBgColor = this.chartStyle.getLastPriceUpColor();
        if (!lastPriceBgColor) {
            lastPriceBgColor = '#32cd32';
        }

        let color = '';
        let changeFromClose = '';
        let changeFromClosePercentagePrefix = '';

        const upColor = this.chartStyle?.getChangeFromCloseUpColor() ?? '';
        const downColor = this.chartStyle?.getChangeFromCloseDownColor() ?? '';
        if (changesFromCloseDollar == 0) {
            color = upColor ? upColor : this.defaultUpColor;
        } else if (changesFromCloseDollar > 0) {
            changeFromClose += '+';
            color = upColor ? upColor : this.defaultUpColor;
        } else {
            changeFromClose += '-';
            changeFromClosePercentagePrefix += '-';
            color = downColor ? downColor : this.defaultDownColor;
        }

        let result = '';
        if (this.displayCFCPercentage) {
            result = `${changeFromClosePercentagePrefix}${FormatHelpers.numberToString(Math.abs(changesFromClosePercentage))}%`;
        } else {
            result = `$${changeFromClose}${FormatHelpers.numberToString(Math.abs(changesFromCloseDollar))}`;
        }

        if (this.changeFromCloseElement) {
            this.changeFromCloseElement.style.color = color;
            this.changeFromCloseElement.setInnerHtml(result);
        }
    }

    private updatePriceLabel() {
        const tradePrice = this.currentBidAskModel.tradePrice;
        const bidPrice = this.currentBidAskModel.bid;
        const askPrice = this.currentBidAskModel.ask;

        // Calculate Order Book values
        this.bidSize = this.currentBidAskModel.bidParticipantSize;
        this.bidCount = this.currentBidAskModel.bidParticipantCount;
        this.askSize = this.currentBidAskModel.askParticipantSize;
        this.askCount = this.currentBidAskModel.askParticipantCount;

        // Calculate percentages based on total volume
        const totalSize = this.bidSize + this.askSize;
        this.bidPercentage = totalSize > 0 ? (this.bidSize / totalSize) * 100 : 50;
        this.askPercentage = totalSize > 0 ? (this.askSize / totalSize) * 100 : 50;

        if (this.isBid(tradePrice, bidPrice)) {
            this.element.querySelector('.bid')?.classList.add('active');
            this.element.querySelector('.ask')?.classList.remove('active');
        } else if (this.isAsk(tradePrice, askPrice)) {
            this.element.querySelector('.ask')?.classList.add('active');
            this.element.querySelector('.bid')?.classList.remove('active');
        }

        let bidFormattedValue = this.getPriceForLabel(bidPrice);
        if (bidFormattedValue == null) {
            bidFormattedValue = `$${FormatHelpers.numberToStringThousandSeparators(bidPrice, true)}`;
        }
        
        const bidElement = this.element.querySelector('.bid .value') as HTMLElement;
        if (bidElement) {
            bidElement.innerText = bidFormattedValue;
        }

        let askFormattedValue = this.getPriceForLabel(askPrice);
        if (askFormattedValue == null) {
            askFormattedValue = `$${FormatHelpers.numberToStringThousandSeparators(askPrice, true)}`;
        }
        const askElement = this.element.querySelector('.ask .value') as HTMLElement;
        if (askElement) {
            askElement.innerText = askFormattedValue;
        }

        // Update bid graph labels (count, price, size)
        const bidGraph = this.element.querySelector('.form-group.graphs .half.green .graph') as HTMLElement;
        if (bidGraph) {
            bidGraph.setAttribute('style', `--graph-left-height: ${this.bidPercentage.toFixed(2)}%`);
        }

        const bidGraphLeft = this.element.querySelector('.form-group.graphs .half.green .labels .left') as HTMLElement;
        if (bidGraphLeft) {
            bidGraphLeft.innerText = `${this.bidCount}`;
        }

        const bidGraphCenter = this.element.querySelector('.form-group.graphs .half.green .labels .center') as HTMLElement;
        if (bidGraphCenter) {
            bidGraphCenter.innerText = `${bidFormattedValue}`;
        }

        const bidGraphRight = this.element.querySelector('.form-group.graphs .half.green .labels .right') as HTMLElement;
        if (bidGraphRight) {
            bidGraphRight.innerText = `${FormatHelpers.numberToStringThousandSeparators(Math.round(this.bidSize), true)}`;
        }

        // Update ask graph labels (count, price, size)
        const askGraph = this.element.querySelector('.form-group.graphs .half.red .graph') as HTMLElement;
        if (askGraph) {
            askGraph.setAttribute('style', `--graph-right-height: ${this.askPercentage.toFixed(2)}%`);
        }

        const askGraphLeft = this.element.querySelector('.form-group.graphs .half.red .labels .left') as HTMLElement;
        if (askGraphLeft) {
            askGraphLeft.innerText = `${this.askCount}`;
        }

        const askGraphCenter = this.element.querySelector('.form-group.graphs .half.red .labels .center') as HTMLElement;
        if (askGraphCenter) {
            askGraphCenter.innerText = `${askFormattedValue}`;
        }

        const askGraphRight = this.element.querySelector('.form-group.graphs .half.red .labels .right') as HTMLElement;
        if (askGraphRight) {
            askGraphRight.innerText = `${FormatHelpers.numberToStringThousandSeparators(Math.round(this.askSize), true)}`;
        }

        const spread = askPrice - bidPrice;
        const spreadElement = this.element.querySelector('.diff-bid-ask') as HTMLElement;
        if (spreadElement) {
            let spreadText = '';

            spreadText = `${FormatHelpers.numberToStringThousandSeparators(spread, false, 2)}`;
            const formatFourDecimal = this.getPriceForLabel(spread);
            if (formatFourDecimal != null) {
                spreadText = formatFourDecimal;
            }
            
            if (spreadText.startsWith('0.')) {
                spreadText = spreadText.substring(1);
            }

            spreadElement.innerHTML = spreadText;
        }
    }

    private formatPriceWithPrecision(price: number): string {
        const decimalPlaces = 4;
        let formatted = FormatHelpers.numberToStringThousandSeparators(price, false, decimalPlaces);
        if (formatted.includes('.')) {
            const parts = formatted.split('.');
            if (parts[1]) {
                let decimalPart = parts[1];
                while (decimalPart.length > 2 && decimalPart.endsWith('0')) {
                    decimalPart = decimalPart.slice(0, -1);
                }
                formatted = parts[0] + '.' + decimalPart;
            }
        }
        
        return formatted;
    }

    private getPriceForLabel(price: number): string {
        const lastPriceValue = this.coloredLastPrice?.getLastPrice() ?? 0;
        if (this.chartStyle && lastPriceValue != 0 && lastPriceValue <= this.chartStyle.advancedOEPTOSFourDecimalLessThan) {
            return FormatHelpers.numberToStringThousandSeparators(price, false, 4);
        }
        
        return FormatHelpers.numberToStringThousandSeparators(price, false, 2);
    }

    private updateSymbolLabel() {
        const symbolElement = this.element.querySelector('.last-price-symbol-wrapper .symbol') as HTMLElement;
        if (symbolElement) {
            symbolElement.innerText = this.symbol;
        }

        if (this.symbolSwitcher && (!this.symbolSwitcher.value || this.symbolSwitcher.value.toLowerCase() != this.symbol.toLowerCase())) {
            this.symbolSwitcher.setValue(this.symbol);
        }
    }

    private dataRows: Array<any> = [];
    private updateDataTable() {
        if (this.dataTable) {
            this.dataTable.clear();

            if (this.bidAskTableData.length < 1) {
                this.dataTable.draw(false);
                return;
            }

            const maxRows = this.chartStyle?.advancedOEPTOSCount || 100;
            this.dataRows = this.bidAskTableData.slice(0, maxRows);

            this.dataRows.forEach((data: BidAskDataModel) => {
                if (this.showDetailedView) {
                    this.dataTable.row.add([
                        data.symbol,
                        data.tradeTime,
                        data.tradePrice,
                        data.tradeSize,
                        data.quoteTime,
                        data.bid,
                        data.ask,
                        data.bidParticipantSize,
                        data.askParticipantSize,
                        data.bidParticipantCount,
                        data.askParticipantCount,
                        data.dollarChange,
                        data.percentChange
                    ]);
                } else {
                    this.dataTable.row.add([
                        data.tradeTime,
                        data.tradePrice,
                        data.tradeSize
                    ]);
                }
            });

            if (this.firstBidAskModel) {
                if (this.showDetailedView) {
                    this.dataTable.row.add([
                        this.firstBidAskModel.symbol,
                        this.firstBidAskModel.tradeTime,
                        this.firstBidAskModel.tradePrice,
                        this.firstBidAskModel.tradeSize,
                        this.firstBidAskModel.quoteTime,
                        this.firstBidAskModel.bid,
                        this.firstBidAskModel.ask,
                        this.firstBidAskModel.bidParticipantSize,
                        this.firstBidAskModel.askParticipantSize,
                        this.firstBidAskModel.bidParticipantCount,
                        this.firstBidAskModel.askParticipantCount,
                        this.firstBidAskModel.dollarChange,
                        this.firstBidAskModel.percentChange
                    ]);
                } else {
                    this.dataTable.row.add([
                        this.firstBidAskModel.tradeTime,
                        this.firstBidAskModel.tradePrice,
                        this.firstBidAskModel.tradeSize
                    ]);
                }
            }

            this.dataTable.draw(false);
        }
    }

    private onResize() {
        window.addEventListener('resize', () => {
            this.removeAllActiveSelectList();
            this.adjustBidAskTableWidthForPopout();
        })
    }

    private onExtreamChange() {
        this.base.tiChartSeries.subscribeRedrawChange(this.id, () => {
            this.checkIfElementOverflow();
        });
    }

    private checkIfElementOverflow() {
        if (this.chartStyle.primitiveMode || this.chartStyle.advancedOEPPopoutMode || this.base.isExtraSmall() || !this.isActive()) {

            if (this.base.isExtraSmall()) {
                this.hide();
            }

            return;
        }

        const child = this.element;
        const parentRect = document.body.getBoundingClientRect();
        const childRect = child.getBoundingClientRect();

        if (
            !(childRect.top >= 0 &&
                childRect.bottom <= parentRect.height &&
                childRect.left >= parentRect.left &&
                childRect.right <= parentRect.right) || !(childRect.width && childRect.height)
        ) {
            // Calculate default top position to ensure element is 20px above window bottom
            const elementHeight = childRect.height || this.element.offsetHeight;
            const windowHeight = window.innerHeight;
            const bottomMargin = 20;

            // Calculate top position: window height - element height - bottom margin
            const calculatedTop = windowHeight - elementHeight - bottomMargin;

            // Ensure the top is not negative (element would be above viewport)
            const safeTop = Math.max(0, calculatedTop);

            this.element.style.left = this.DEFAULT_LEFT;
            this.element.style.top = `${safeTop}px`;
            this.saveUIState();
        }
    }

    public saveTradeMenuState(defaultTradeMenu: string) {
        if (!this.chartStyle) return;

        if (!this.chartStyle.advancedOEPStateModel) {
            this.chartStyle.advancedOEPStateModel = new AdvancedOEPStateModel();
        }

        this.chartStyle.advancedOEPStateModel.defaultTradeMenu = defaultTradeMenu;
        this.saveUIState();
    }

    private previousSavedState: AdvancedOEPStateModel | null = null;
    private saveUIState() {
        if (!this.base.chartService || !this.chartStyle || !this.enabled) return;

        const isOpen = this.element.classList.contains('active');
        const top = this.element.style.top?.replace('px', '') ?? '';
        const left = this.element.style.left?.replace('px', '') ?? '';
        const account = this.accountRendered && !this.preventPersistingAccount ? this.model.account : this.chartStyle.newTradeUIAccount;
        const defaultTradeMenu = this.chartStyle.advancedOEPStateModel.defaultTradeMenu;
        const model = new AdvancedOEPStateModel({
            open: isOpen,
            top: top,
            left: left,
            account: account,
            defaultTradeMenu: defaultTradeMenu,
            autoSend: this.autoSendElement.checked,
            displayCFCPercentage: this.displayCFCPercentage,
            expandedTable: this.element.classList.contains('table-expanded'),
            expandedLimitIn: !this.limitInElement?.classList.contains('hidden')
        });

        if (this.previousSavedState && JSON.stringify(this.previousSavedState) === JSON.stringify(model)) {
            return;
        }

        this.previousSavedState = model;
        this.base.chartService.requestSaveAdvancedOEPState(model);
    }

    private onBodyClick() {
        document.body.addEventListener('click', (e: any) => {
            if (!e || !e.target || typeof e.target.classList === 'undefined') {
                return;
            }

            if (!(e.target.classList.contains('select-list') || !!e.target.closest('.select-list'))) {
                this.removeAllActiveSelectList();
            }
        });
    }

    private onDrag() {
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

        this.element.addEventListener('mousedown', (e: MouseEvent) => {
            if (!this.dragEnabled) {
                return;
            }

            if (this.dragging) {
                return;
            }

            const targetElement = e.target as HTMLElement;
            if (targetElement && targetElement.classList.contains('no-d') || !!targetElement.closest('.no-d')) {
                return;
            }

            this.dragging = true;

            pos3 = e.clientX;
            pos4 = e.clientY;
        });

        document.body.addEventListener('mousemove', (e: MouseEvent) => {
            if (!this.dragEnabled) {
                return;
            }

            if (!this.dragging) {
                return;
            }

            pos1 = pos3 - e.clientX;
            pos2 = pos4 - e.clientY;
            pos3 = e.clientX;
            pos4 = e.clientY;

            const newLeft = this.element.offsetLeft - pos1;
            const newTop = this.element.offsetTop - pos2;

            // Check if the new position is within the container
            if (
                newLeft >= 0 &&
                newTop >= 0 &&
                newLeft + this.element.offsetWidth <= this.base.body.offsetWidth &&
                newTop + this.element.offsetHeight <= this.base.body.offsetHeight
            ) {
                this.element.style.left = newLeft + 'px';
                this.element.style.top = newTop + 'px';
                this.element.classList.add('dragging');
                this.moving = true;
            }
        });

        document.body.addEventListener('mouseleave', () => {
            if (!this.dragEnabled) {
                return;
            }

            if (!this.dragging) {
                return;
            }

            this.afterDragging();
        });

        document.body.addEventListener('mouseup', () => {
            if (!this.dragEnabled) {
                return;
            }

            if (!this.dragging) {
                return;
            }

            this.afterDragging();
        });
    }

    private afterDragging() {
        this.dragging = false;
        this.element.classList.remove('dragging');

        setTimeout(() => {
            this.saveUIState();
            this.moving = false;
            setTimeout(() => {
                this.checkIfElementOverflow();
            }, 300);
        }, 500);
    }

    private onArrowExpandClick() {
        this.arrowExpand?.addEventListener('click', (e: MouseEvent) => {
            // If this is a manual click (not triggered programmatically), reset auto-expand flag
            if (e.isTrusted) {
                this.tosAutoExpanded = false;
                this.cancelScheduledAutoCollapse();
            }
            this.element.classList.toggle('table-expanded');
            this.adjustBidAskTableWidthForPopout();
            this.saveUIState();
        });
    }

    private adjustBidAskTableWidthForPopout() {
        if (!this.chartStyle?.advancedOEPPopoutMode) return;

        const bidAskTableWrapper = this.element.querySelector<HTMLElement>('.bid-ask-table-wrapper');
        if (!bidAskTableWrapper) return;

        const isExpanded = this.element.classList.contains('table-expanded');
        if (!isExpanded) {
            bidAskTableWrapper.style.removeProperty('width');
            bidAskTableWrapper.style.removeProperty('right');
            this.arrowExpand?.style.removeProperty('left');
            return;
        }

        const componentRect = this.element.getBoundingClientRect();
        const availableWidth = window.innerWidth - componentRect.right;
        const tableWidth = Math.max(218, availableWidth);

        // Use 'important' priority so inline styles override dev-mode CSS !important rules
        bidAskTableWrapper.style.setProperty('width', `${tableWidth}px`, 'important');
        bidAskTableWrapper.style.setProperty('right', `-${tableWidth}px`, 'important');
        if (this.arrowExpand) {
            this.arrowExpand.style.setProperty('left', `${280 + tableWidth}px`, 'important');
        }
        this.dataTable?.columns.adjust();
    }

    /**
     * Schedules auto-collapse of the TOS (Time of Sales) table after a symbol is loaded.
     * Only triggers if the TOS was auto-expanded (not manually by user).
     * @param enableAutoCollapse - Whether the auto-collapse feature is enabled
     * @param autoCollapseDelaySeconds - Number of seconds to wait before auto-collapsing
     */
    private scheduleAutoCollapseTOS(enableAutoCollapse: boolean, autoCollapseDelaySeconds: number) {
        this.cancelScheduledAutoCollapse();

        if (!enableAutoCollapse || !this.tosAutoExpanded) {
            return;
        }

        this.autoCollapseTimeoutId = window.setTimeout(() => {
            if (this.tosAutoExpanded && this.element.classList.contains('table-expanded')) {
                this.tosAutoExpanded = false;
                this.arrowExpand?.click();
            }
            this.autoCollapseTimeoutId = null;
        }, autoCollapseDelaySeconds * 1000);
    }

    private cancelScheduledAutoCollapse() {
        if (this.autoCollapseTimeoutId !== null) {
            window.clearTimeout(this.autoCollapseTimeoutId);
            this.autoCollapseTimeoutId = null;
        }
    }

    private dataTable: any;
    private initDataTable() {
        if (this.dataTable) {
            this.dataTable.destroy();
        }

        const ctx = this;
        const columns = this.getColumns();
        this.dataTable = $('#bid-ask-table').DataTable({
            searching: false,
            bLengthChange: false,
            ordering: true,
            bInfo: false,
            paging: false,
            autoWidth: false,
            scrollX: true,
            scrollY: '285px',
            order: [[this.showDetailedView ? 1 : 0, 'desc']],
            columns: columns,
            createdRow: function (row, data, dataIndex) {
                if (ctx.dataRows[dataIndex]) {
                    const price = ctx.dataRows[dataIndex].tradePrice;
                    const bidPrice = ctx.currentBidAskModel.bid;
                    const askPrice = ctx.currentBidAskModel.ask;
                    if (ctx.isBid(price, bidPrice)) {
                        $(row).addClass('bid-row');
                    } else if (ctx.isAsk(price, askPrice)) {
                        $(row).addClass('ask-row');
                    }
                }
            },
            data: []
        });
    }

    private isBid(tradePrice: number, bidPrice: number): boolean {
        let adjustedBidPrice = FormatHelpers.numberToStringThousandSeparators(bidPrice, false, 2);
        let adjustedTradePrice = FormatHelpers.numberToStringThousandSeparators(tradePrice, false, 2);
        if (this.chartStyle?.advancedOEPTOSFourDecimalLessThan && (tradePrice <= this.chartStyle.advancedOEPTOSFourDecimalLessThan)) {
            adjustedBidPrice = FormatHelpers.numberToStringThousandSeparators(bidPrice, false, 4);
            adjustedTradePrice = FormatHelpers.numberToStringThousandSeparators(tradePrice, false, 4);
        }
        
        const bidPriceNum = parseFloat(adjustedBidPrice);
        const tradePriceNum = parseFloat(adjustedTradePrice);
        return tradePriceNum <= bidPriceNum;
    }

    private isAsk(tradePrice: number, askPrice: number): boolean {
        let adjustedAskPrice = FormatHelpers.numberToStringThousandSeparators(askPrice, false, 2);
        let adjustedTradePrice = FormatHelpers.numberToStringThousandSeparators(tradePrice, false, 2);
        if (this.chartStyle?.advancedOEPTOSFourDecimalLessThan && (tradePrice <= this.chartStyle.advancedOEPTOSFourDecimalLessThan)) {
            adjustedAskPrice = FormatHelpers.numberToStringThousandSeparators(askPrice, false, 4);
            adjustedTradePrice = FormatHelpers.numberToStringThousandSeparators(tradePrice, false, 4);
        }

        const askPriceNum = parseFloat(adjustedAskPrice);
        const tradePriceNum = parseFloat(adjustedTradePrice);
        return tradePriceNum >= askPriceNum;
    }

    private getColumns() {
        const ctx = this;
        return this.showDetailedView ? [
            {
                title: 'Symbol',
                width: '4%',
                render: function (data, type, row, meta) {
                    return data;
                }
            },
            {
                title: 'Trade Time',
                width: '10%',
                class: 'time',
                render: function (data, type, row, meta) {
                    const zone = ctx.base.tiChartSeries?.chartStyleSettings?.timeZoneId;
                    if (zone) {
                        const timeInZoneConverted = DateHelpers.convertFromTimezone(data, zone);
                        return timeInZoneConverted.toFormat('HH:mm:ss');
                    }
                    return DateHelpers.format('HH:MM:SS', data);
                }
            },
            {
                title: 'Trade Price',
                width: '10%',
                class: 'price dt-left',
                className: 'dt-left',
                orderable: false,
                render: function (data, type, row, meta) {
                    if (data == null) return ``;

                    return ctx.formatPriceWithPrecision(data);
                }
            },
            {
                title: 'Trade Size',
                width: '10%',
                class: 'size dt-right',
                className: 'dt-right',
                orderable: false,
                render: function (data, type, row, meta) {
                    if (data == null) return ``;
                    return `${FormatHelpers.numberToStringThousandSeparators(data, true)}`;
                }
            },
            {
                title: 'Quote Time',
                width: '10%',
                class: 'time',
                orderable: false,
                render: function (data, type, row, meta) {
                    const zone = ctx.base.tiChartSeries?.chartStyleSettings?.timeZoneId;
                    if (zone) {
                        const timeInZoneConverted = DateHelpers.convertFromTimezone(data, zone);
                        return timeInZoneConverted.toFormat('HH:mm:ss.SSS');
                    }
                    return DateHelpers.format('HH:MM:SS.fff', data);
                }
            },
            {
                title: 'Bid',
                width: '10%',
                orderable: false,
                render: function (data, type, row, meta) {
                    if (data == null) return ``;
                    return `${FormatHelpers.numberToStringThousandSeparators(data, false)}`;
                }
            },
            {
                title: 'Ask',
                width: '10%',
                orderable: false,
                render: function (data, type, row, meta) {
                    if (data == null) return ``;
                    return `${FormatHelpers.numberToStringThousandSeparators(data, false)}`;
                }
            },
            {
                title: 'Bid Size',
                width: '10%',
                className: 'dt-right',
                orderable: false,
                render: function (data, type, row, meta) {
                    if (data == null) return ``;
                    return `${FormatHelpers.numberToStringThousandSeparators(data, true)}`;
                }
            },
            {
                title: 'Ask Size',
                width: '10%',
                className: 'dt-right',
                orderable: false,
                render: function (data, type, row, meta) {
                    if (data == null) return ``;
                    return `${FormatHelpers.numberToStringThousandSeparators(data, true)}`;
                }
            },
            {
                title: 'Bid Part..',
                width: '8%',
                orderable: false,
                render: function (data, type, row, meta) {
                    return data ?? '';
                }
            },
            {
                title: 'Ask Part..',
                width: '8%',
                orderable: false,
                render: function (data, type, row, meta) {
                    return data ?? '';
                }
            },
            {
                title: '$ Chg',
                width: '8%',
                orderable: false,
                render: function (data, type, row, meta) {
                    if (data == null) return '';
                    return ctx.formatPriceWithPrecision(data);
                }
            },
            {
                title: '% Chg',
                width: '8%',
                orderable: false,
                render: function (data, type, row, meta) {
                    if (data == null) return '';
                    return `${FormatHelpers.numberToStringThousandSeparators(data, false, 2)}%`;
                }
            }
        ] : [
            {
                title: 'Time',
                width: '33.33%',
                class: 'time',
                render: function (data, type, row, meta) {
                    const zone = ctx.base.tiChartSeries?.chartStyleSettings?.timeZoneId;
                    if (zone) {
                        const timeInZoneConverted = DateHelpers.convertFromTimezone(data, zone);
                        const timeAsString = timeInZoneConverted.toFormat('HH:mm:ss');
                        return timeAsString;
                    }
                    return DateHelpers.format('HH:MM:SS', data);
                }
            },
            {
                title: 'Price',
                width: '33.33%',
                class: 'price dt-left',
                className: 'dt-left',
                orderable: false,
                render: function (data, type, row, meta) {
                    if (data == null) return ``;

                    return ctx.formatPriceWithPrecision(data);
                }
            },
            {
                title: 'Size',
                width: '33.33%',
                class: 'size',
                orderable: false,
                render: function (data, type, row, meta) {
                    if (data == null) return ``;
                    return `${FormatHelpers.numberToStringThousandSeparators(data, true)}`;
                }
            }
        ];
    }

    private renderHtml() {
        const arrow = TradeComponent.arrow;
        const html = `
            <div class="avoep-container">
                <div class="wrapper">
                    <div class="head">
                        <div class="bid">
                            <span class="label">Bid</span>
                            <span class="value"></span>
                        </div>
                        <div class="last-price-symbol-wrapper">
                            <div class="symbol-wrapper no-d" title="Click to change Symbols">
                                <span class="symbol"></span>
                            </div>
                            <div class="symbol-lookup-wrapper no-d">
                                <input type="text" id="avoep-chart-symbol-lookup" class="avoep-chart-symbol" />
                                <span class="icon-close">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17" width="10" height="10" fill="currentColor">
                                        <path d="m.58 1.42.82-.82 15 15-.82.82z"></path>
                                        <path d="m.58 15.58 15-15 .82.82-15 15z"></path>
                                    </svg>
                                </span>
                            </div>
                            <span class="change-from-close no-d">
                                <span class="last-price">
                                    <span class="value"></span>
                                </span>
                                <span class="pnl-value">&nbsp;</span>
                            </span>
                        </div>
                        <div class="ask">
                            <span class="label">Ask</span>
                            <span class="value"></span>
                        </div>
                    </div>
                    <div class="form-group graphs mgt-6 g-2">
                        <div class="half green">
                            <div class="graph">
                            </div>
                            <div class="labels">
                                <span class="left"></span>
                                <span class="center"></span>
                                <span class="right"></span>
                            </div>
                        </div>
                        <div class="diff-bid-ask no-d"></div>
                        <div class="half red">
                            <div class="graph">
                            </div>
                            <div class="labels">
                                <span class="left"></span>
                                <span class="center"></span>
                                <span class="right"></span>
                            </div>
                        </div>
                    </div> 
                    <div class="form-group mgt-10">
                        <div class="select-list quote-type no-d">
                            <p>Shares</p>
                            ${arrow}
                            <ul>
                                <li data-value="Shares">Shares</li>
                                <li data-value="Dollars">Dollars</li>
                            </ul>
                        </div>
                        <div class="quote-quantity-input no-d">
                            <input type="text" inputmode="decimal" pattern="[0-9]*" />
                            <button class="up">
                                <span>+</span>
                            </button>
                            <button class="down">
                                <span>-</span>
                            </button>
                        </div>
                        <div class="quantity-group no-d">
                            <span class="value" data-value="100">100</span>
                            <span class="value" data-value="1000">1k</span>
                            <span class="value" data-value="10000">10k</span>
                        </div>
                    </div>
                    <div class="form-group mgt-6">
                        <div class="select-list accounts  no-d">
                            <p></p>
                            ${arrow}
                            <ul></ul>
                        </div>
                        <div class="checkbox-input mgl-12  no-d">           
                            <input type="checkbox" id="auto-send" />
                            <label for="auto-send" class="mgl-4">auto send</label>
                        </div>
                    </div>
                    <div class="form-group mgt-6 g-4">
                        <div class="half inline-flex">
                            <span class="advanced-trade-btn red no-d" data-value="SellBid">Sell BID</span>
                        </div>
                        <div class="half inline-flex">
                            <span class="advanced-trade-btn green no-d" data-value="BuyAsk">Buy ASK</span>
                        </div>
                    </div>
                    <div class="form-group mgt-6 g-4">
                        <div class="half inline-flex">
                            <span class="advanced-trade-btn green no-d" data-value="JoinBid">Join BID</span>
                        </div>
                        <div class="half inline-flex">
                            <span class="advanced-trade-btn red no-d" data-value="JoinAsk">Join ASK</span>
                        </div>
                    </div>
                    <div class="form-group mgt-6 g-4">
                        <div class="half inline-flex">
                            <span class="advanced-trade-btn red no-d" data-value="SellMarket">Sell MARKET</span>
                        </div>
                        <div class="half inline-flex">
                            <span class="advanced-trade-btn green no-d" data-value="BuyMarket">Buy MARKET</span>
                        </div>
                    </div>
                    <div class="form-group mgt-6 g-4">
                        <div class="half inline-flex">
                            <span class="advanced-trade-btn orange no-d" data-value="CancelOrder">Cancel Order</span>
                        </div>
                        <div class="half inline-flex">
                            <span class="advanced-trade-btn orange no-d" data-value="LimitIn">Limit Order</span>
                        </div>
                    </div>
                    <div class="form-group mgt-6 limit-in b hidden">
                        <span class="button buy no-d">BUY</span>
                        <span class="button sell no-d">SELL</span>
                        <div class="limit-in-input no-d">
                            <input type="text" inputmode="decimal" pattern="[0-9]*" />
                            <button class="up">
                                <span>+</span>
                            </button>
                            <button class="down">
                                <span>-</span>
                            </button>
                        </div>
                        <span class="button submit no-d">Submit</span>
                    </div>
                    <div class="form-group mgt-6">
                        <span class="advanced-trade-label"></span>
                    </div>
                </div>
                <div class="bid-ask-table-wrapper">
                    <div class="export no-d" >
                        <span class="export-btn no-d" title="Export to CSV">
                            <svg fill="#ffffff" width="14px" height="14px" viewBox="0 0 1920 1920" stroke="#ffffff">
                                <path d="M790.706 338.824v112.94H395.412c-31.06 0-56.47 25.3-56.47 56.471v744.509c17.73-6.325 36.592-10.391 56.47-10.391h1129.412c19.877 0 38.738 4.066 56.47 10.39V508.236c0-31.171-25.412-56.47-56.47-56.47h-395.295V338.824h395.295c93.402 0 169.411 76.009 169.411 169.411v1242.353c0 93.403-76.01 169.412-169.411 169.412H395.412C302.009 1920 226 1843.99 226 1750.588V508.235c0-93.402 76.01-169.411 169.412-169.411h395.294Zm734.118 1016.47H395.412c-31.06 0-56.47 25.299-56.47 56.47v338.824c0 31.172 25.41 56.47 56.47 56.47h1129.412c31.058 0 56.47-25.298 56.47-56.47v-338.823c0-31.172-25.412-56.47-56.47-56.47ZM1016.622-.023v880.151l246.212-246.325 79.85 79.85-382.532 382.644-382.645-382.644 79.85-79.85L903.68 880.128V-.022h112.941ZM564.824 1468.235c-62.344 0-112.942 50.71-112.942 112.941s50.598 112.942 112.942 112.942c62.343 0 112.94-50.71 112.94-112.942 0-62.23-50.597-112.94-112.94-112.94Z" fill-rule="evenodd"/> 
                            </svg>
                        </span>
                    </div>
                    <table id="bid-ask-table" class="cell-border"></table>
                </div>
                <div class="arrow-expand">
                    <svg>
                        <use xlink:href="${this.base.basePath}assetfiles/img/double-arrow-down.svg#double-arrow-down-obj"></use>
                    </svg>
                </div>
            </div>
        `;

        const div = document.createElement("DIV");
        div.className = 'advanced-trade-component';

        div.style.left = '0px';
        div.style.top = '0px';
        div.innerHTML = html;

        this.element = div;

        this.base.chartWrapper?.insertAdjacentElement('beforeend', this.element);
        this.changeFromCloseElement = this.element.querySelector('.pnl-value') as HTMLElement;
        this.accountSelectListElement = this.element.querySelector('.select-list.accounts');
        this.autoSendElement = this.element.querySelector('#auto-send') as HTMLInputElement;
        this.limitInGroup = this.element.querySelector<HTMLElement>('.form-group.limit-in');
        this.arrowExpand = this.element.querySelector<HTMLElement>('.arrow-expand');
        this.symbolSwitcher = this.element.findFirst('.avoep-chart-symbol') as HTMLInputElement | null;
        this.limitInElement = this.element.querySelector<HTMLElement>('.form-group.limit-in');
        this.exportButtonElement = this.element.querySelector<HTMLElement>('.export-btn');
        this.coloredLastPrice = new ColoredLastPriceComponent(this.element.querySelector('.last-price-symbol-wrapper .last-price .value') as HTMLElement, ColoredLastPriceSource.AdvancedOEP, this.base);
    }
}